import { useState, useEffect, useRef } from "react";
import { P, FF, inputBase } from "../../styles/tokens";
import { isFijoVencido, isFijoPorVencer } from "../../utils/gastos";
import { fmt } from "../../utils/format";
import { api } from "../../services/api";
import AccountSelect from "./AccountSelect";
import MoneyInput from "./MoneyInput";
import { reportar } from "../../utils/reportar";

export default function FijosInputRow({ row, onChange, onDelete, cuentas, computedSaldos }) {
  const [local, setLocal] = useState(row);
  const [showAjuste, setShowAjuste] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const dirty = useRef(false);
  const localRef = useRef(local);
  localRef.current = local;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (row.id !== local.id) {
      setLocal(prev => ({ ...prev, id: row.id }));
    }
    if (!dirty.current) setLocal(row);
  }, [row]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const forceCommit = () => {
      if (dirty.current) {
        dirty.current = false;
        onChangeRef.current(localRef.current);
      }
    };
    window.addEventListener("beforeunload", forceCommit);
    return () => {
      forceCommit();
      window.removeEventListener("beforeunload", forceCommit);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (next) => { dirty.current = true; setLocal(next); };
  const commit = () => {
    if (!dirty.current) return;
    dirty.current = false;
    onChange(local);
  };
  const handleKey = (e) => { if (e.key === "Enter") e.target.blur(); /* blur triggers commit */ };
  const editAndCommit = (next) => { dirty.current = false; onChange(next); setLocal(next); };

  const vencido = isFijoVencido(local);
  const porVencer = isFijoPorVencer(local);
  const diffMonto = local.pagado && local.montoPagado != null ? Number(local.montoPagado) - Number(local.monto) : 0;
  const statusToken = local.pagado
    ? diffMonto === 0 ? P.ingresos : diffMonto > 0 ? P.danger : P.warn
    : vencido ? P.danger : porVencer ? P.warn : null;

  const [saldoAlert, setSaldoAlert] = useState(null);
  const togglePago = () => {
    if (!local.pagado) {
      // Requiere cuenta asignada para pagar
      if (!local.cuentaId) {
        setSaldoAlert("Asigna una cuenta primero");
        setTimeout(() => setSaldoAlert(null), 3000);
        return;
      }
      // Validar saldo suficiente
      if (computedSaldos) {
        const saldo = computedSaldos[local.cuentaId];
        if (saldo !== undefined && saldo < Number(local.monto)) {
          setSaldoAlert(`Saldo insuficiente: ${fmt(saldo)} disponible`);
          setTimeout(() => setSaldoAlert(null), 3000);
          return;
        }
      }
      editAndCommit({ ...local, pagado: true, montoPagado: local.monto });
    }
    else { editAndCommit({ ...local, pagado: false, montoPagado: null }); setShowAjuste(false); }
  };

  const isDirty = local.concepto !== row.concepto || local.monto !== row.monto || local.cuentaId !== row.cuentaId
    || local.fechaLimite !== row.fechaLimite || local.montoPagado !== row.montoPagado;

  return (
    <div style={{ padding: "10px 0", borderBottom: `1px solid ${P.border}`, opacity: row.esRecurrente ? 0.8 : 1 }}>
      {/* Línea 1: concepto + monto + acciones */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {row.esRecurrente && <span style={{ fontSize: 11, flexShrink: 0 }}>🔁</span>}
        <input
          aria-label="Concepto del gasto fijo"
          value={local.concepto}
          onChange={(e) => edit({ ...local, concepto: e.target.value })}
          onBlur={commit}
          onKeyDown={handleKey}
          placeholder="Descripción…"
          style={{ flex: 1, border: "none", background: "transparent", color: P.text, fontSize: 14, fontFamily: FF, outline: "none", padding: "2px 0", fontWeight: 500, minWidth: 0 }}
        />
        <div style={{ position: "relative", width: 96, flexShrink: 0 }}>
          <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 10, pointerEvents: "none" }}>$</span>
          <MoneyInput
            aria-label="Monto del gasto fijo"
            value={local.monto}
            onChange={(v) => edit({ ...local, monto: v })}
            onBlur={commit}
            onKeyDown={handleKey}
            style={{ ...inputBase, paddingLeft: 18, fontWeight: 700, textAlign: "right", paddingRight: 6, fontSize: 13, color: P.fijos.text }}
          />
        </div>
        <button
          onClick={togglePago}
          aria-label="Marcar como pagado"
          style={{
            flexShrink: 0, padding: "4px 10px", borderRadius: 9, cursor: "pointer", fontFamily: FF, fontSize: 11, fontWeight: 700,
            border: `1.5px solid ${local.pagado ? P.ingresos.base : P.border}`,
            background: local.pagado ? P.ingresos.pastel : P.surface, color: local.pagado ? P.ingresos.text : P.textSm,
          }}
        >
          {local.pagado ? "✓" : "Pagar"}
        </button>
        {local.pagado && (
          <button
            onClick={() => setShowAjuste(!showAjuste)}
            style={{
              flexShrink: 0, padding: "4px 8px", borderRadius: 9, cursor: "pointer", fontFamily: FF, fontSize: 11, fontWeight: 600,
              border: `1.5px solid ${statusToken && statusToken !== P.ingresos ? statusToken.tint : P.border}`,
              background: showAjuste ? P.fijos.pastel : "transparent",
              color: statusToken && statusToken !== P.ingresos ? statusToken.text : P.textXs,
            }}
          >
            {diffMonto > 0 ? "↑" : diffMonto < 0 ? "↓" : "=$"}
          </button>
        )}
        {isDirty && (
          <button
            onMouseDown={(e) => e.preventDefault()}
            onClick={commit}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid #10B981`, background: "#D1FAE5", color: "#065F46", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontWeight: 700 }}
          >
            ✓
          </button>
        )}
        {confirming ? (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "#991B1B" }}>¿Seguro?</span>
            <button onClick={() => { onDelete(); setConfirming(false); }}
              aria-label="Confirmar eliminar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #FECACA", background: "#FEF2F2", color: "#DC2626", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✓</button>
            <button onClick={() => setConfirming(false)}
              aria-label="Cancelar"
              style={{ width: 36, height: 36, borderRadius: 10, border: "1.5px solid #E5E7EB", background: "#F3F4F6", color: "#6B7280", fontSize: 13, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>✕</button>
          </div>
        ) : (
          <button
            onClick={() => setConfirming(true)}
            style={{ width: 36, height: 36, borderRadius: 10, border: `1.5px solid ${P.border}`, background: "transparent", color: P.textXs, cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            ×
          </button>
        )}
      </div>
      {saldoAlert && (
        <div style={{ padding: "4px 10px", marginTop: 4, borderRadius: 8, background: "#FEF2F2", border: "1px solid #FECACA", fontSize: 11, fontWeight: 600, color: "#DC2626", fontFamily: FF }}>{saldoAlert}</div>
      )}
      {/* Línea 2: cuenta + fecha límite */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, paddingLeft: row.esRecurrente ? 24 : 0 }}>
        <AccountSelect value={local.cuentaId} required onChange={(v) => {
          editAndCommit({ ...local, cuentaId: v });
          if (row.esRecurrente && row.recurrenteId) api.recurrentes.update(row.recurrenteId, { cuenta_id: v || null }).catch(reportar("cambiar cuenta del recurrente"));
        }} cuentas={cuentas} />
        <div
          style={{
            display: "flex", alignItems: "center", gap: 3, flexShrink: 0,
            background: vencido && !local.pagado ? P.danger.pastel : porVencer && !local.pagado ? P.warn.pastel : P.bg,
            borderRadius: 8, padding: "4px 4px 4px 7px",
            border: `1px solid ${vencido && !local.pagado ? P.danger.tint : porVencer && !local.pagado ? P.warn.tint : P.border}`,
          }}
        >
          <span style={{ fontSize: 10 }}>📅</span>
          <select
            aria-label="Dia limite de pago"
            value={local.fechaLimite || ""}
            onChange={(e) => {
              editAndCommit({ ...local, fechaLimite: e.target.value });
              if (row.esRecurrente && row.recurrenteId) api.recurrentes.update(row.recurrenteId, { dia_mes: Number(e.target.value) || null }).catch(reportar("cambiar día del recurrente"));
            }}
            style={{
              width: 38, border: "none", background: "transparent",
              color: vencido && !local.pagado ? P.danger.text : porVencer && !local.pagado ? P.warn.text : P.text,
              fontSize: 11, fontFamily: FF, fontWeight: 700, outline: "none", textAlign: "center",
              cursor: "pointer", appearance: "none", WebkitAppearance: "none", padding: "2px 0",
            }}
          >
            <option value="">—</option>
            {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
        </div>
      </div>
      {local.pagado && showAjuste && (
        <div style={{ marginTop: 6, padding: "8px 10px", background: P.fijos.pastel, borderRadius: 10, border: `1px solid ${P.fijos.tint}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: P.fijos.text, fontFamily: FF }}>Monto real:</span>
          <button
            onClick={() => editAndCommit({ ...local, montoPagado: local.monto })}
            style={{
              padding: "3px 8px", borderRadius: 7, cursor: "pointer", fontFamily: FF, fontSize: 11, fontWeight: 600,
              border: `1.5px solid ${Number(local.montoPagado) === Number(local.monto) ? P.ingresos.base : P.border}`,
              background: Number(local.montoPagado) === Number(local.monto) ? P.ingresos.pastel : P.surface,
              color: Number(local.montoPagado) === Number(local.monto) ? P.ingresos.text : P.textSm,
            }}
          >
            = {fmt(local.monto)}
          </button>
          <div style={{ position: "relative", flex: 1, minWidth: 110, maxWidth: 150 }}>
            <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", color: P.textXs, fontSize: 10 }}>$</span>
            <MoneyInput
              value={local.montoPagado}
              onChange={(v) => edit({ ...local, montoPagado: v })}
              onBlur={commit}
              onKeyDown={handleKey}
              placeholder={String(local.monto || 0)}
              style={{
                ...inputBase, paddingLeft: 18, fontWeight: 800, fontSize: 12,
                color: diffMonto > 0 ? P.danger.text : diffMonto < 0 ? P.warn.text : P.ingresos.text, background: P.surface,
              }}
            />
          </div>
          {local.montoPagado != null && diffMonto !== 0 && (
            <span style={{ fontSize: 11, fontWeight: 700, fontFamily: FF, color: diffMonto > 0 ? P.danger.text : P.warn.text }}>
              {diffMonto > 0 ? `↑ ${fmt(diffMonto)}` : `↓ ${fmt(Math.abs(diffMonto))}`}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
